import React, { useState } from "react";
import { Card, Typography, Row, Col, Button } from "antd";
import { DragOutlined, DeleteOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

// Type definitions
interface Step {
  id: string;
  name: string;
  color: string;
  borderColor: string;
  uniqueId?: string;
}

// Available steps that can be dragged
const availableSteps: Step[] = [
  {
    id: "customer-info",
    name: "Customer Information",
    color: "#e6f7ff",
    borderColor: "#91d5ff",
  },
  {
    id: "vehicle-details",
    name: "Vehicle Details",
    color: "#f6ffed",
    borderColor: "#b7eb8f",
  },
  {
    id: "vin-number",
    name: "VIN Number",
    color: "#fff2e8",
    borderColor: "#ffbb96",
  },
  {
    id: "license-plate",
    name: "License Plate",
    color: "#f9f0ff",
    borderColor: "#d3adf7",
  },
  {
    id: "odometer-reading",
    name: "Odometer Reading",
    color: "#fff1f0",
    borderColor: "#ffa39e",
  },
  {
    id: "key-handover",
    name: "Key Handover",
    color: "#f0f9ff",
    borderColor: "#87e8de",
  },
];

const StepCard = ({
  step,
  isWorkflow = false,
  onRemove,
  index,
}: {
  step: Step;
  isWorkflow?: boolean;
  onRemove?: (index: number) => void;
  index?: number;
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    e.dataTransfer.setData("text/plain", JSON.stringify(step));
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <Card
      size="small"
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{
        marginBottom: 12,
        backgroundColor: step.color,
        border: `2px solid ${step.borderColor}`,
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
        transition: "all 0.2s ease",
      }}
      bodyStyle={{ padding: "12px 16px" }}
    >
      <Row align="middle" justify="space-between">
        <Col>
          <Row align="middle" gutter={8}>
            {isWorkflow && (
              <Col>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    backgroundColor: "#1890ff",
                    color: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {typeof index === "number" ? index + 1 : ""}
                </div>
              </Col>
            )}
            <Col>
              <DragOutlined style={{ color: "#8c8c8c", marginRight: 8 }} />
            </Col>
            <Col>
              <Text strong>{step.name}</Text>
            </Col>
          </Row>
        </Col>
        {isWorkflow && (
          <Col>
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => index !== undefined && onRemove?.(index)}
              style={{ color: "#ff4d4f" }}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};

const DropZone = ({
  children,
  onDrop,
  isEmpty,
}: {
  children?: React.ReactNode;
  onDrop: (step: Step) => void;
  isEmpty: boolean;
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const stepData = JSON.parse(e.dataTransfer.getData("text/plain"));
    onDrop(stepData);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        minHeight: "400px",
        padding: "16px",
        border: isDragOver ? "2px dashed #1890ff" : "2px dashed #d9d9d9",
        borderRadius: "8px",
        backgroundColor: isDragOver
          ? "#f0f9ff"
          : isEmpty
          ? "#fafafa"
          : "transparent",
        transition: "all 0.2s ease",
      }}
    >
      {isEmpty ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
            color: "#8c8c8c",
          }}
        >
          <DragOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
          <Text type="secondary" style={{ fontSize: "16px" }}>
            Drag steps here to build your workflow
          </Text>
        </div>
      ) : (
        children
      )}
    </div>
  );
};

const App = () => {
  const fixedFirstStep: Step = {
    id: "1",
    name: "Vehicle Registration",
    color: "#fff7e6ff",
    borderColor: "#ffd991ff",
    uniqueId: "rego-step",
  };

  const [workflowSteps, setWorkflowSteps] = useState<Step[]>([fixedFirstStep]);

  const handleAddStep = (step: Step) => {
    if (step.uniqueId === "rego-step") return;

    const newStep = {
      ...step,
      uniqueId: `${step.id}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
    };
    setWorkflowSteps([...workflowSteps, newStep]);
  };

  const handleRemoveStep = (index: number) => {
    if (workflowSteps[index].uniqueId === fixedFirstStep.uniqueId) return;

    setWorkflowSteps(workflowSteps.filter((_, i) => i !== index));
  };

  const handleClearWorkflow = () => {
    setWorkflowSteps([fixedFirstStep]);
  };

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Title level={2} style={{ textAlign: "center" }}>
          Demo Workflow Editor
        </Title>
        <Text
          type="secondary"
          style={{
            display: "block",
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          Created by Noah Melamed for ProofTec
        </Text>

        <Row gutter={32}>
          {/* Available Steps */}
          <Col span={8}>
            <Card
              title={
                <Title level={4} style={{ margin: 0 }}>
                  Available Steps
                </Title>
              }
              style={{ height: "fit-content" }}
            >
              <Text
                type="secondary"
                style={{ marginBottom: "16px", display: "block" }}
              >
                Drag these steps to build your workflow. You can use each step
                multiple times.
              </Text>
              {availableSteps.map((step) => (
                <StepCard key={step.id} step={step} />
              ))}
            </Card>
          </Col>

          {/* Workflow Builder */}
          <Col span={16}>
            <Card
              title={
                <Row justify="space-between" align="middle">
                  <Col>
                    <Title level={4} style={{ margin: 0 }}>
                      Your Workflow ({workflowSteps.length} steps)
                    </Title>
                  </Col>
                  {workflowSteps.length > 0 && (
                    <Col>
                      <Button danger onClick={handleClearWorkflow}>
                        Clear All
                      </Button>
                    </Col>
                  )}
                </Row>
              }
            >
              <DropZone
                onDrop={handleAddStep}
                isEmpty={workflowSteps.length === 0}
              >
                {workflowSteps.map((step, index) => (
                  <StepCard
                    key={step.uniqueId}
                    step={step}
                    index={index}
                    isWorkflow={true}
                    onRemove={handleRemoveStep}
                  />
                ))}
              </DropZone>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default App;
